import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Check, Grid3X3, DoorOpen, Hammer, Bath, Phone, Home } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";

const ServicesPage = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

  const services = [
    {
      id: "windows",
      title: "Windows",
      icon: Grid3X3,
      hero: "https://images.unsplash.com/photo-1631277190979-1704e8c7d574?w=1200",
      description: "Transform your home with energy-efficient Low-E Argon Gas windows that reduce utility bills and increase comfort year-round.",
      benefits: [
        "Lower energy bills with Low-E Argon Gas filled glass",
        "Reduce outside noise for a quieter home",
        "UV protection to prevent furniture fading",
        "Enhanced home security with modern locking systems",
        "Increased property value",
        "Wide variety of styles to match any home"
      ],
      types: [
        { name: "Double-Hung Windows", desc: "Classic style with Low-E Argon Gas" },
        { name: "Casement Windows", desc: "Side-hinged windows that open outward" },
        { name: "Bay & Bow Windows", desc: "Project outward for more space and light" },
        { name: "Sliding Windows", desc: "Horizontal operation, great for tight spaces" },
        { name: "Picture Windows", desc: "Fixed windows for maximum light and views" },
        { name: "Basement Egress Windows", desc: "Code-compliant options for below-grade" }
      ]
    },
    {
      id: "doors",
      title: "Doors",
      icon: DoorOpen,
      hero: "https://images.pexels.com/photos/7587880/pexels-photo-7587880.jpeg?w=1200",
      description: "Make a lasting first impression with beautiful sliding patio doors, secure entry doors with sidelights, and stylish interior options.",
      benefits: [
        "Enhanced curb appeal and home value",
        "Improved security with modern lock systems",
        "Better insulation and energy efficiency",
        "Weather protection from the elements",
        "Custom sizes and styles available",
        "Professional installation guaranteed"
      ],
      types: [
        { name: "Entry Doors", desc: "Fiberglass, steel, and wood with sidelights" },
        { name: "Patio Doors", desc: "Sliding and French door styles" },
        { name: "Storm Doors", desc: "Extra protection and ventilation" },
        { name: "Garage Doors", desc: "Insulated and decorative options" },
        { name: "Interior Doors", desc: "Matching styles throughout your home" },
        { name: "Security Doors", desc: "Heavy-duty protection for peace of mind" }
      ]
    },
    {
      id: "kitchens",
      title: "Kitchens",
      icon: Hammer,
      hero: "https://images.unsplash.com/photo-1762529716215-afd7400d7bd4?w=1200",
      description: "Create your dream kitchen with custom center islands, white shaker cabinets, beautiful pendant lighting, and modern appliances.",
      benefits: [
        "Increased home value (up to 70% ROI)",
        "Improved functionality and storage",
        "Modern appliance integration",
        "Better lighting with pendant fixtures",
        "Custom design to fit your lifestyle",
        "Quality materials built to last"
      ],
      types: [
        { name: "Full Remodels", desc: "Complete kitchen transformation" },
        { name: "White Shaker Cabinets", desc: "Classic timeless cabinet style" },
        { name: "Center Islands", desc: "With pendant lighting & seating" },
        { name: "Countertop Installation", desc: "Granite, quartz, and marble" },
        { name: "Backsplash & Tile", desc: "Custom tile work and designs" },
        { name: "Appliance Installation", desc: "Professional setup and integration" }
      ]
    },
    {
      id: "bathrooms",
      title: "Bathrooms",
      icon: Bath,
      hero: "https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=1200",
      description: "Turn your bathroom into a personal spa with luxurious fixtures, modern vanities, and elegant tile work.",
      benefits: [
        "Create a relaxing spa-like retreat",
        "Improve functionality and storage",
        "Update outdated fixtures and finishes",
        "Add accessibility features as needed",
        "Increase home value",
        "Fix water damage and prevent future issues"
      ],
      types: [
        { name: "Full Renovations", desc: "Complete bathroom makeover" },
        { name: "Walk-In Showers", desc: "Modern, accessible shower designs" },
        { name: "Tub Replacements", desc: "Soaking tubs and tub-shower combos" },
        { name: "Vanity Installation", desc: "Custom and pre-fab vanity options" },
        { name: "Tile & Flooring", desc: "Ceramic, porcelain, and luxury vinyl" },
        { name: "Fixture Upgrades", desc: "Faucets, toilets, and accessories" }
      ]
    },
    {
      id: "new-construction",
      title: "New Construction",
      icon: Home,
      hero: "https://images.pexels.com/photos/7601163/pexels-photo-7601163.jpeg?w=1200",
      description: "Build your dream home from the ground up with our comprehensive new construction services.",
      benefits: [
        "Custom design to match your vision",
        "Quality materials and craftsmanship",
        "Full project management from start to finish",
        "Energy-efficient building practices",
        "Transparent pricing and timelines",
        "Licensed and insured construction team"
      ],
      types: [
        { name: "Custom Homes", desc: "Fully customized home builds" },
        { name: "Single Family Homes", desc: "Traditional and modern designs" },
        { name: "Multi-Unit Buildings", desc: "Duplexes and small multi-family" },
        { name: "Additions", desc: "Room additions and expansions" },
        { name: "Garage Construction", desc: "Attached and detached garages" },
        { name: "Outdoor Living", desc: "Decks, patios, and outdoor spaces" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="services-page">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-orange-400 font-bold tracking-wider text-sm">OUR SERVICES</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mt-4 mb-6 font-['Chivo']">
            Expert Home Improvement
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            From windows and doors to complete kitchen and bathroom renovations, 
            we deliver quality craftsmanship on every project.
          </p>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Tabs defaultValue="windows" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent mb-12" data-testid="services-tabs">
              {services.map((service) => (
                <TabsTrigger
                  key={service.id}
                  value={service.id}
                  className="flex items-center gap-2 px-6 py-3 rounded-sm border-2 border-slate-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:border-orange-500 font-bold transition-all"
                  data-testid={`tab-${service.id}`}
                >
                  <service.icon className="w-5 h-5" />
                  {service.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {services.map((service) => (
              <TabsContent key={service.id} value={service.id} className="animate-fade-in">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                  {/* Image */}
                  <div className="relative rounded-sm overflow-hidden shadow-xl">
                    <img
                      src={service.hero}
                      alt={service.title}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-8">
                      <h2 className="text-3xl font-bold text-white font-['Chivo']">{service.title}</h2>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <h3 className="text-xl font-bold text-slate-900 mb-4 font-['Chivo']">Benefits</h3>
                    <ul className="space-y-3 mb-8">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-orange-600" />
                          </div>
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <h3 className="text-xl font-bold text-slate-900 mb-4 font-['Chivo']">Services We Offer</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                      {service.types.map((type, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-sm border border-slate-200">
                          <h4 className="font-bold text-slate-900 mb-1">{type.name}</h4>
                          <p className="text-sm text-slate-500">{type.desc}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button 
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-sm font-bold px-8 py-6 btn-orange"
                            data-testid={`quote-btn-${service.id}`}
                          >
                            Get Free {service.title} Quote
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-lg">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold font-['Chivo']">
                              Request a Free {service.title} Quote
                            </DialogTitle>
                          </DialogHeader>
                          <QuoteForm onSuccess={() => setQuoteDialogOpen(false)} />
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="outline"
                        className="border-2 border-slate-200 text-slate-700 hover:border-blue-600 hover:text-blue-600 rounded-sm font-bold px-8 py-6"
                        asChild
                      >
                        <a href="tel:708-856-1055">
                          <Phone className="mr-2 w-5 h-5" />
                          Call 708-856-1055
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-400 font-bold tracking-wider text-sm">HOW IT WORKS</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4 mb-6 font-['Chivo']">
              Our Simple Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Free Consultation", desc: "We visit your home, discuss your needs, and take measurements." },
              { step: "02", title: "Detailed Quote", desc: "Receive a comprehensive, no-obligation quote within 24 hours." },
              { step: "03", title: "Expert Installation", desc: "Our skilled team completes the work efficiently and cleanly." },
              { step: "04", title: "Final Walkthrough", desc: "We ensure you're 100% satisfied with the completed project." }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-black text-white font-['Chivo']">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Chivo']">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
